/**
 * The placement picker (1.O-1 deliverable 5): when the anchor page has several placements (a
 * `pick` choice, from the preview or from a 422 at commit), the GM chooses which one the rule hangs
 * on. A listbox in 1.K-1's order (role, label, then date): arrows move, Enter or a click chooses.
 */
import { type CompiledCalendar, formatTick } from '@codex/calendar';
import type { AnchorOptionV1, PlacementV1 } from '@codex/contracts';
import { type KeyboardEvent, useId, useState } from 'react';
import { placementDate } from './pill-model.js';
import { pickerLabel, roleWord, statusWord } from './words.js';

export interface PlacementPickerProps {
  readonly title: string;
  readonly options: readonly AnchorOptionV1[];
  /** The anchor page's placements, for each option's date. */
  readonly placements: readonly PlacementV1[];
  readonly calendar: CompiledCalendar;
  readonly chosenId: string | null;
  readonly onChoose: (id: string) => void;
}

export function optionDate(
  option: AnchorOptionV1,
  placements: readonly PlacementV1[],
  calendar: CompiledCalendar,
): string {
  const placement = placements.find((p) => p.id === option.id);
  if (placement) return placementDate(calendar, placement);
  return option.lo === null ? 'Undated' : (formatTick(calendar, option.lo, 'day') ?? 'Undated');
}

export function PlacementPicker({
  title,
  options,
  placements,
  calendar,
  chosenId,
  onChoose,
}: PlacementPickerProps) {
  const id = useId();
  const chosenIndex = options.findIndex((o) => o.id === chosenId);
  const [active, setActive] = useState(Math.max(chosenIndex, 0));
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.key === 'ArrowDown' ? 1 : event.key === 'ArrowUp' ? -1 : 0;
    if (step !== 0) {
      event.preventDefault();
      setActive((active + step + options.length) % options.length);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const option = options[active];
      if (option) onChoose(option.id);
    }
  };
  return (
    <>
      <span className="cx-when-picker__title">{pickerLabel(title)}</span>
      <div
        role="listbox"
        tabIndex={0}
        aria-label={pickerLabel(title)}
        aria-activedescendant={`${id}-${active}`}
        className="cx-when-picker"
        data-testid="when.picker.list"
        onKeyDown={onKeyDown}
      >
        {options.map((option, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the listbox owns the keyboard (aria-activedescendant)
          <div
            key={option.id}
            id={`${id}-${index}`}
            role="option"
            tabIndex={-1}
            aria-selected={option.id === chosenId}
            data-active={index === active || undefined}
            className="cx-when-picker__option"
            onClick={() => {
              setActive(index);
              onChoose(option.id);
            }}
          >
            <span className="cx-when-picker__role">{roleWord(option.role)}</span>
            <span>{option.label ?? 'Untitled'}</span>
            <span
              className="cx-state"
              data-tone={option.effectiveStatus === 'canon' ? 'success' : 'neutral'}
            >
              {statusWord(option.effectiveStatus)}
            </span>
            <span className="cx-when-picker__date">{optionDate(option, placements, calendar)}</span>
          </div>
        ))}
      </div>
    </>
  );
}
